// src/firebase/crosswordService.ts

// Import Firebase modules properly
import { initializeApp, getApps } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  getDocs,
  getDoc,
  doc,
  query, 
  where,
  orderBy,
  limit,
  deleteDoc,
  updateDoc,
  writeBatch,
  startAfter,
  DocumentData
} from 'firebase/firestore';

// Your Firebase configuration - Replace with your actual Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyCrt82BXhbNYHflGlUERp36MpZgYooKQdg",
  authDomain: "literastic.firebaseapp.com",
  projectId: "literastic",
  storageBucket: "literastic.firebasestorage.app",
  messagingSenderId: "566135467303",
  appId: "1:566135467303:web:9440278379f4ec53cd0d02",
  measurementId: "G-SC55DYJW44"
};

// Initialize Firebase only if it hasn't been initialized already
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
const db = getFirestore(app);

// Data Models
export interface WordClue {
  word: string;
  clue: string;
}

export interface CrosswordPuzzle {
  id?: string;
  title: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  words: WordClue[];
  createdAt?: any; // Firestore timestamp
}

// Vocabulary Database - Organize words by categories
const vocabularyDatabase: {[key: string]: WordClue[]} = {
  animals: [
    { word: 'ELEPHANT', clue: 'Large gray animal with a trunk' },
    { word: 'GIRAFFE', clue: 'Tall animal with a long neck' },
    { word: 'PENGUIN', clue: 'Black and white bird that cannot fly' },
    { word: 'DOLPHIN', clue: 'Smart marine mammal known for its clicks and whistles' },
    { word: 'TIGER', clue: 'Large cat with orange fur and black stripes' },
    { word: 'KANGAROO', clue: 'Australian animal that hops and has a pouch' },
    { word: 'ZEBRA', clue: 'Horse-like animal with black and white stripes' },
    { word: 'MONKEY', clue: 'Primate that climbs trees and has a long tail' },
    { word: 'SHARK', clue: 'Large fish with sharp teeth and a fin on its back' },
    { word: 'TURTLE', clue: 'Slow-moving reptile with a hard shell' }
  ],
  
  food: [
    { word: 'APPLE', clue: 'Red or green fruit that grows on trees' },
    { word: 'PIZZA', clue: 'Italian dish with cheese, sauce, and toppings' },
    { word: 'CARROT', clue: 'Orange vegetable that helps you see in the dark' },
    { word: 'BANANA', clue: 'Yellow fruit with a peel' },
    { word: 'COOKIE', clue: 'Sweet baked treat, often with chocolate chips' },
    { word: 'BREAD', clue: 'Baked food made from flour and water' },
    { word: 'CHEESE', clue: 'Dairy product that can be yellow or white' },
    { word: 'POTATO', clue: 'Vegetable that can be mashed, fried, or baked' },
    { word: 'ORANGE', clue: 'Citrus fruit with the same name as its color' },
    { word: 'PASTA', clue: 'Italian food made from flour and water, like spaghetti' }
  ],
  
  school: [
    { word: 'PENCIL', clue: 'Writing tool with lead inside' },
    { word: 'TEACHER', clue: 'Person who helps students learn' },
    { word: 'LIBRARY', clue: 'Place with many books to read or borrow' },
    { word: 'SCIENCE', clue: 'Subject about nature and how things work' },
    { word: 'RECESS', clue: 'Break time to play outside during school' },
    { word: 'HOMEWORK', clue: 'Schoolwork that you do at home' },
    { word: 'ERASER', clue: 'Tool used to remove pencil marks' },
    { word: 'STUDENT', clue: 'Person who goes to school to learn' },
    { word: 'BACKPACK', clue: 'Bag used to carry books and supplies' },
    { word: 'DESK', clue: 'Furniture where students sit and work' }
  ],
  
  colors: [
    { word: 'YELLOW', clue: 'Color of the sun' },
    { word: 'PURPLE', clue: 'Color made by mixing red and blue' },
    { word: 'GREEN', clue: 'Color of grass and leaves' },
    { word: 'ORANGE', clue: 'Color between red and yellow' },
    { word: 'BLACK', clue: 'Darkest color, opposite of white' },
    { word: 'WHITE', clue: 'Color of snow' },
    { word: 'BLUE', clue: 'Color of the sky on a clear day' },
    { word: 'RED', clue: 'Color of fire and apples' },
    { word: 'BROWN', clue: 'Color of chocolate and tree trunks' },
    { word: 'PINK', clue: 'Light red color' }
  ],
  
  nature: [
    { word: 'MOUNTAIN', clue: 'Very tall landform with a peak' },
    { word: 'OCEAN', clue: 'Vast body of salt water' },
    { word: 'FOREST', clue: 'Area with many trees' },
    { word: 'RIVER', clue: 'Natural flowing water that moves to a lake or ocean' },
    { word: 'CLOUD', clue: 'White or gray shape in the sky made of water droplets' },
    { word: 'BEACH', clue: 'Sandy shore by the ocean' },
    { word: 'FLOWER', clue: 'Colorful part of a plant that blooms' },
    { word: 'RAINBOW', clue: 'Colorful arch in the sky after rain' },
    { word: 'LEAF', clue: 'Green part of a plant that grows from a stem' },
    { word: 'STAR', clue: 'Bright point of light in the night sky' }
  ],
  
  body: [
    { word: 'HEAD', clue: 'Top part of your body with your brain inside' },
    { word: 'HAND', clue: 'Part of your body with fingers at the end of your arm' },
    { word: 'FOOT', clue: 'Part of your body at the end of your leg' },
    { word: 'EYE', clue: 'Body part you use to see' },
    { word: 'EAR', clue: 'Body part you use to hear' },
    { word: 'NOSE', clue: 'Body part you use to smell' },
    { word: 'MOUTH', clue: 'Body part you use to eat and talk' },
    { word: 'HEART', clue: 'Organ that pumps blood through your body' },
    { word: 'BONE', clue: 'Hard part inside your body that gives it structure' },
    { word: 'TEETH', clue: 'Hard white things in your mouth used for chewing' }
  ],
  
  clothing: [
    { word: 'SHIRT', clue: 'Garment worn on the upper body' },
    { word: 'PANTS', clue: 'Garment worn on the legs' },
    { word: 'SHOES', clue: 'Footwear that protects your feet' },
    { word: 'HAT', clue: 'Worn on your head for protection or style' },
    { word: 'COAT', clue: 'Heavy outer garment worn in cold weather' },
    { word: 'DRESS', clue: 'One-piece garment typically worn by girls and women' },
    { word: 'SOCK', clue: 'Worn on your feet inside shoes' },
    { word: 'GLOVE', clue: 'Covers your hand to keep it warm' },
    { word: 'SCARF', clue: 'Worn around the neck for warmth' },
    { word: 'JACKET', clue: 'Outer garment with sleeves and usually a zipper' }
  ],
  
  transportation: [
    { word: 'CAR', clue: 'Four-wheeled vehicle that families drive' },
    { word: 'TRAIN', clue: 'Vehicle that runs on tracks and has many cars' },
    { word: 'PLANE', clue: 'Flying vehicle with wings' },
    { word: 'BOAT', clue: 'Vehicle that travels on water' },
    { word: 'BUS', clue: 'Large vehicle that carries many passengers on roads' },
    { word: 'BIKE', clue: 'Two-wheeled vehicle you pedal' },
    { word: 'TRUCK', clue: 'Large vehicle used to carry heavy loads' },
    { word: 'SUBWAY', clue: 'Underground train in a city' },
    { word: 'ROCKET', clue: 'Vehicle that travels to space' },
    { word: 'SHIP', clue: 'Large vessel for traveling on the ocean' }
  ],
  
  weather: [
    { word: 'RAIN', clue: 'Water that falls from clouds in drops' },
    { word: 'SNOW', clue: 'Frozen water that falls from the sky in winter' },
    { word: 'WIND', clue: 'Moving air that you can feel but cannot see' },
    { word: 'CLOUD', clue: 'White or gray shape in the sky made of water droplets' },
    { word: 'STORM', clue: 'Bad weather with rain, lightning, and thunder' },
    { word: 'SUN', clue: 'Bright star that gives us light and heat during the day' },
    { word: 'COLD', clue: 'Low temperature that makes you shiver' },
    { word: 'HOT', clue: 'High temperature that makes you sweat' },
    { word: 'FOG', clue: 'Cloud that touches the ground making it hard to see' },
    { word: 'FROST', clue: 'Thin layer of ice that forms when it is very cold' }
  ]
};

// Generate a random crossword puzzle
function generateRandomCrossword(id: number): CrosswordPuzzle {
  // Pick a random category
  const categories = Object.keys(vocabularyDatabase);
  const randomCategory = categories[Math.floor(Math.random() * categories.length)];
  
  // Get all words from the category
  const allWords = vocabularyDatabase[randomCategory];
  
  // Shuffle the words
  const shuffledWords = [...allWords].sort(() => Math.random() - 0.5);
  
  // Take a random number of words (between 5-10)
  const numberOfWords = Math.floor(Math.random() * 6) + 5; // 5 to 10 words
  const selectedWords = shuffledWords.slice(0, numberOfWords);
  
  // Determine difficulty based on word length
  let difficulty: 'easy' | 'medium' | 'hard';
  const avgLength = selectedWords.reduce((sum, w) => sum + w.word.length, 0) / selectedWords.length;
  
  if (avgLength < 5) {
    difficulty = 'easy';
  } else if (avgLength < 7) {
    difficulty = 'medium';
  } else {
    difficulty = 'hard';
  }
  
  return {
    title: `${randomCategory.charAt(0).toUpperCase() + randomCategory.slice(1)} Puzzle ${id}`,
    category: randomCategory,
    difficulty,
    words: selectedWords,
    createdAt: new Date()
  };
}

// Add a single puzzle to Firebase
export async function addPuzzleToFirebase(puzzle: CrosswordPuzzle) {
  try {
    const docRef = await addDoc(collection(db, 'crosswordPuzzles'), puzzle);
    console.log('Puzzle added with ID: ', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error adding puzzle: ', error);
    throw error;
  }
}

// Add multiple puzzles to Firebase (batch operation)
export async function addMultiplePuzzlesToFirebase(puzzles: CrosswordPuzzle[]) {
  try {
    // Firestore allows a maximum of 500 operations per batch
    const BATCH_SIZE = 500;
    let batchCount = 0;
    let operationsCount = 0;
    let batch = writeBatch(db);
    
    for (let i = 0; i < puzzles.length; i++) {
      const newDocRef = doc(collection(db, 'crosswordPuzzles'));
      batch.set(newDocRef, puzzles[i]);
      operationsCount++;
      
      // When we reach the batch limit, commit and create a new batch
      if (operationsCount === BATCH_SIZE) {
        await batch.commit();
        batch = writeBatch(db);
        operationsCount = 0;
        batchCount++;
        console.log(`Batch ${batchCount} committed with ${BATCH_SIZE} operations`);
      }
    }
    
    // Commit any remaining operations
    if (operationsCount > 0) {
      await batch.commit();
      batchCount++;
      console.log(`Final batch ${batchCount} committed with ${operationsCount} operations`);
    }
    
    console.log(`Added ${puzzles.length} puzzles to Firebase in ${batchCount} batches`);
    return true;
  } catch (error) {
    console.error('Error adding multiple puzzles: ', error);
    throw error;
  }
}

// Generate 100 puzzles and upload to Firebase
export async function generateAndUpload100Puzzles() {
  try {
    const puzzles = Array.from({ length: 100 }, (_, i) => generateRandomCrossword(i + 1));
    await addMultiplePuzzlesToFirebase(puzzles);
    console.log('Successfully generated and uploaded 100 puzzles');
    return true;
  } catch (error) {
    console.error('Error generating and uploading puzzles: ', error);
    throw error;
  }
}

// Get all puzzles
export async function getAllPuzzles() {
  try {
    const querySnapshot = await getDocs(collection(db, 'crosswordPuzzles'));
    const puzzles: CrosswordPuzzle[] = [];
    
    querySnapshot.forEach((docSnapshot) => {
      puzzles.push({ id: docSnapshot.id, ...docSnapshot.data() } as CrosswordPuzzle);
    });
    
    return puzzles;
  } catch (error) {
    console.error('Error getting all puzzles: ', error);
    throw error;
  }
}

// Get puzzle by ID
export async function getPuzzleById(puzzleId: string) {
  try {
    const docRef = doc(db, 'crosswordPuzzles', puzzleId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as CrosswordPuzzle;
    } else {
      console.log('No such puzzle!');
      return null;
    }
  } catch (error) {
    console.error('Error getting puzzle by ID: ', error);
    throw error;
  }
}

// Get puzzles by category
export async function getPuzzlesByCategory(category: string) {
  try {
    const q = query(
      collection(db, 'crosswordPuzzles'), 
      where('category', '==', category)
    );
    
    const querySnapshot = await getDocs(q);
    const puzzles: CrosswordPuzzle[] = [];
    
    querySnapshot.forEach((docSnapshot) => {
      puzzles.push({ id: docSnapshot.id, ...docSnapshot.data() } as CrosswordPuzzle);
    });
    
    return puzzles;
  } catch (error) {
    console.error('Error getting puzzles by category: ', error);
    throw error;
  }
}

// Get puzzles by difficulty
export async function getPuzzlesByDifficulty(difficulty: 'easy' | 'medium' | 'hard') {
  try {
    const q = query(
      collection(db, 'crosswordPuzzles'), 
      where('difficulty', '==', difficulty)
    );
    
    const querySnapshot = await getDocs(q);
    const puzzles: CrosswordPuzzle[] = [];
    
    querySnapshot.forEach((docSnapshot) => {
      puzzles.push({ id: docSnapshot.id, ...docSnapshot.data() } as CrosswordPuzzle);
    });
    
    return puzzles;
  } catch (error) {
    console.error('Error getting puzzles by difficulty: ', error);
    throw error;
  }
}

// Get puzzles with pagination (for better performance)
export async function getPuzzlesPaginated(pageSize: number = 10, lastVisible: any = null) {
  try {
    let q;
    
    if (lastVisible) {
      q = query(
        collection(db, 'crosswordPuzzles'),
        orderBy('createdAt', 'desc'),
        startAfter(lastVisible),
        limit(pageSize)
      );
    } else {
      q = query(
        collection(db, 'crosswordPuzzles'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      );
    }
    
    const querySnapshot = await getDocs(q);
    const puzzles: CrosswordPuzzle[] = [];
    
    querySnapshot.forEach((docSnapshot) => {
      puzzles.push({ id: docSnapshot.id, ...docSnapshot.data() } as CrosswordPuzzle);
    });
    
    // Get the last visible document for pagination
    const lastVisibleDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    
    return {
      puzzles,
      lastVisible: lastVisibleDoc
    };
  } catch (error) {
    console.error('Error getting paginated puzzles: ', error);
    throw error;
  }
}

// Update a puzzle
export async function updatePuzzle(puzzleId: string, updatedData: Partial<CrosswordPuzzle>) {
  try {
    const puzzleRef = doc(db, 'crosswordPuzzles', puzzleId);
    await updateDoc(puzzleRef, updatedData as DocumentData);
    console.log('Puzzle updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating puzzle: ', error);
    throw error;
  }
}

// Delete a puzzle
export async function deletePuzzle(puzzleId: string) {
  try {
    await deleteDoc(doc(db, 'crosswordPuzzles', puzzleId));
    console.log('Puzzle deleted successfully');
    return true;
  } catch (error) {
    console.error('Error deleting puzzle: ', error);
    throw error;
  }
}

// Record when a user completes a puzzle
export async function recordPuzzleCompletion(userId: string, puzzleId: string, timeToComplete: number) {
  try {
    const completionData = {
      userId,
      puzzleId,
      timeToComplete, // in seconds
      completedAt: new Date()
    };
    
    await addDoc(collection(db, 'puzzleCompletions'), completionData);
    console.log('Puzzle completion recorded');
    return true;
  } catch (error) {
    console.error('Error recording puzzle completion: ', error);
    throw error;
  }
}

// Get puzzles completed by a user
export async function getPuzzlesCompletedByUser(userId: string) {
  try {
    const q = query(
      collection(db, 'puzzleCompletions'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const completions: any[] = [];
    
    querySnapshot.forEach((docSnapshot) => {
      completions.push({ id: docSnapshot.id, ...docSnapshot.data() });
    });
    
    return completions;
  } catch (error) {
    console.error('Error getting completed puzzles: ', error);
    throw error;
  }
}

// Initialize crossword database with 100 puzzles if empty
export async function initializeCrosswordDatabase() {
  try {
    // Check if we already have puzzles
    const existingPuzzles = await getAllPuzzles();
    
    if (existingPuzzles.length > 0) {
      console.log(`Database already contains ${existingPuzzles.length} puzzles.`);
      return existingPuzzles;
    }
    
    // If no puzzles exist, generate and upload 100 puzzles
    console.log('No puzzles found. Generating 100 new puzzles...');
    await generateAndUpload100Puzzles();
    
    // Verify puzzles were added successfully
    const newPuzzles = await getAllPuzzles();
    console.log(`Successfully initialized database with ${newPuzzles.length} puzzles.`);
    return newPuzzles;
  } catch (error) {
    console.error('Error initializing database: ', error);
    throw error;
  }
}